import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { useQueryClient } from 'react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import { AiSearchWindow } from './components/ai-search-window';
import { CollapsibleReasoning } from './components/collapsible-reasoning';
import { CompactingMessage } from './components/compacting-message';
import { ComposingMessage } from './components/composing-message';
import { KimErrorState } from './components/kim-error-state';
import { KimMessage } from './components/kim-message';
import { ReferenceKey } from './components/reference-key';
import { ReferenceSidePanel } from './components/reference-side-panel';
import { ScrollToBottomButton } from './components/scroll-to-bottom-button';
import { SharePopover } from './components/share-popover';
import { SurveyBlock as SurveyBlockComponent } from './components/survey-block';
import { VerifyDisclaimer } from './components/verify-disclaimer';
import { KimThreadStatus } from '../../../../../../../../../../backend/src/matter/subset/thread/kim-thread-status';
import { Permission } from '../../../../../../../../../../backend/src/shared/action-registry';
import {
  ContentBlock,
  ArtifactBlock,
  DocumentDownloadOptions,
  KimMessageType,
  ReferenceType,
  StreamBlock,
  SurveyBlock,
} from '../../../../../../../../../../backend/src/types/types';
import { ExportType } from '../../../../../../../../../../backend/src/types/types';
import { ThreadDownloadDialog } from '../../kim/:threadId/components/answer/thread-download-dialog';
import { KimInput } from '../components/kim-input';
import { useKimFileUpload } from '../hooks/use-kim-file-upload';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { BaseExportEvent } from '@/api/hooks/useExportEvents';
import { KimStreamChunk, useKimStream } from '@/api/hooks/useKimStream';
import { useExportKimToWord } from '@/api/mutations/useExportKimToWord';
import { useExportKimWithDocuments } from '@/api/mutations/useExportKimWithDocuments';
import { useSendKimInterrupt } from '@/api/mutations/useSendKimInterrupt';
import { useSendKimMessage } from '@/api/mutations/useSendKimMessage';
import { useGetKimMessages } from '@/api/queries/useGetKimMessages';
import { useGetKimThread } from '@/api/queries/useGetKimThread';
import { QueryEntity } from '@/api/types';
import { KimThreadFile } from '@/api/types/backend-dto-types';
import { useUserContext } from '@/Contexts/User';
import { useHasPermission } from '@/hooks/useHasPermission';
import { GenericExportProgressModal } from '@/new/components/dialogs/export-progress-dialog';
import { DocMentionsInputHandle, extractKimMentions } from '@/new/components/doc-mentions-input';
import { Icon } from '@/new/components/icon';
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle, EmptyDescription } from '@/new/components/shadcn/empty';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { cn } from '@/new/lib/utils';

/** Merge artifact into content: if last block is same id and version, append content; otherwise push. */
function mergeArtifactIntoContent(content: ContentBlock[], artifact: ArtifactBlock): void {
  const last = content[content.length - 1];
  if (last?.type === 'artifact' && last.id === artifact.id && last.versionNumber === artifact.versionNumber) {
    const merged: ArtifactBlock = {
      type: 'artifact',
      id: artifact.id,
      name: artifact.name ?? last.name,
      artifactType: artifact.artifactType,
      content: [...(last as ArtifactBlock).content, ...artifact.content],
      versionNumber: artifact.versionNumber,
    };
    content[content.length - 1] = merged;
    return;
  }
  content.push(artifact);
}

const isKimThreadActive = (status?: KimThreadStatus) =>
  status === KimThreadStatus.InProgress || status === KimThreadStatus.Composing;

const isKimThreadSettled = (status?: KimThreadStatus) =>
  status === KimThreadStatus.Complete || status === KimThreadStatus.Ready || status === KimThreadStatus.Failed;

export function NewKimThread() {
  const { matterId, subsetId, threadId } = useParams();
  const queryClient = useQueryClient();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const trackingElementRef = useRef<HTMLDivElement>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const kimInputRef = useRef<DocMentionsInputHandle>(null);

  const [isInFlight, setIsInFlight] = useState(false);
  const {
    data: thread,
    isLoading: threadLoading,
    error: threadError,
  } = useGetKimThread(matterId, subsetId, threadId, {
    refetchInterval: isInFlight ? 5000 : false,
  });
  const { user } = useUserContext();
  const threadName = thread?.name;

  const {
    data: messages,
    isLoading: messagesLoading,
    isSuccess: messagesLoaded,
    fetchFresh: fetchFreshMessages,
  } = useGetKimMessages(matterId, subsetId, threadId);

  const initialLastMessageId = useMemo(() => {
    if (!messagesLoaded || !messages?.length) return undefined;

    const lastMessage = messages[messages.length - 1];
    return String(new Date(lastMessage.createdDate).getTime());
  }, [messages, messagesLoaded]);

  const { chunks: rawChunks } = useKimStream({
    matterId: matterId!,
    subsetId: subsetId!,
    threadId: threadId!,
    enabled: Boolean(matterId && subsetId && threadId && messagesLoaded),
    initialLastMessageId,
  });

  // Filter out stream chunks already persisted to DB to avoid a flash of
  // duplicate content when messages are refetched (e.g. after artifact download).
  const chunks = useMemo(() => {
    if (!initialLastMessageId) return rawChunks;
    const threshold = Number(initialLastMessageId);
    return rawChunks.filter((chunk) => chunk.timestamp > threshold);
  }, [rawChunks, initialLastMessageId]);

  useEffect(() => {
    const content = scrollContentRef.current;
    if (!content) return;

    const recalcSpacer = () => {
      const container = scrollContainerRef.current;
      const tracking = trackingElementRef.current;
      if (!container || !tracking) return;

      const userMessages = container.querySelectorAll('[data-user-message]');
      const lastUserMessage = userMessages[userMessages.length - 1];
      if (!lastUserMessage) return;

      const containerRect = container.getBoundingClientRect();
      const trackingRect = tracking.getBoundingClientRect();
      const lastUserMsgRect = lastUserMessage.getBoundingClientRect();

      // Convert to scroll-independent positions relative to container top
      const scrollTop = container.scrollTop;
      const trackingTop = trackingRect.top - containerRect.top + scrollTop;
      const lastUserMsgTop = lastUserMsgRect.top - containerRect.top + scrollTop;
      const distance = trackingTop - lastUserMsgTop;

      if (spacerRef.current) {
        spacerRef.current.style.height = `${Math.max(128, container.clientHeight - distance)}px`;
      }

      checkScrollToBottom();
    };
    const observer = new ResizeObserver(recalcSpacer);
    observer.observe(content);
    // Also observe the scroll container itself so the spacer recalculates when
    // the container resizes (e.g. input expanding shrinks it via flex layout).
    const container = scrollContainerRef.current;
    if (container) observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const userScrolledUpRef = useRef(false);
  const userScrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const lastScrollTopRef = useRef(0);

  const checkScrollToBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    setShowScrollToBottom(distanceFromBottom > 200);

    // Suppress auto-scroll for 1500ms after user scrolls up
    if (container.scrollTop < lastScrollTopRef.current) {
      userScrolledUpRef.current = true;
      clearTimeout(userScrollTimeoutRef.current);
      userScrollTimeoutRef.current = setTimeout(() => {
        userScrolledUpRef.current = false;
      }, 1500);
    }
    lastScrollTopRef.current = container.scrollTop;
  }, []);

  const [sidePanel, setSidePanel] = useState<{
    isOpen: boolean;
    activeId: string | null;
    activeType: ReferenceType | null;
  }>({ isOpen: false, activeId: null, activeType: null });

  const openSidePanel = useCallback((id: string, referenceType: ReferenceType) => {
    setSidePanel({ isOpen: true, activeId: id, activeType: referenceType });
  }, []);

  const closeSidePanel = useCallback(() => {
    setSidePanel({ isOpen: false, activeId: null, activeType: null });
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  const highlightArtifactId = searchParams.get('artifact');

  // Reset scroll and close side panel when switching threads, unless coming from the artifacts page
  useEffect(() => {
    if (highlightArtifactId) return;
    scrollContainerRef.current?.scrollTo({ top: 0 });
    closeSidePanel();
  }, [threadId]);

  const spacerRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState('');
  const { mutate: sendMessage, isLoading: isSending } = useSendKimMessage(matterId, subsetId, threadId);
  const { mutate: sendInterrupt } = useSendKimInterrupt(matterId, subsetId, threadId);
  const { uploadedFiles, isUploading, addFiles, removeFile, clearFiles } = useKimFileUpload(matterId);

  // Download state
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showExportProgressModal, setShowExportProgressModal] = useState(false);
  const [isCreatingExport, setIsCreatingExport] = useState(false);
  const [exportError, setExportError] = useState<string | undefined>(undefined);
  const [currentExportId, setCurrentExportId] = useState<string | null>(null);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [downloadMessageId, setDownloadMessageId] = useState<string | null>(null);
  const [downloadArtifactId, setDownloadArtifactId] = useState<string | undefined>(undefined);

  const { mutate: exportToWord } = useExportKimToWord(matterId, subsetId, threadId);
  const { startExport, getProgressEndpoint } = useExportKimWithDocuments(matterId, subsetId, threadId);

  const handleDownloadClick = useCallback((artifactId?: string) => {
    setDownloadArtifactId(artifactId);
    setShowDownloadModal(true);
  }, []);

  const isContentBlock = (block: StreamBlock): block is ContentBlock => block.type !== 'end';

  const { artifactsById, latestArtifactId } = useMemo(() => {
    const map = new Map<string, ArtifactBlock & { published: boolean | undefined }>();

    // Build a lookup of published status from DB message artifacts (keyed by id-versionNumber)
    const publishedLookup = new Map<string, boolean>();
    messages?.forEach((msg) => {
      msg.artifacts?.forEach((artifact) => {
        publishedLookup.set(artifact.id + '-' + artifact.versionNumber, artifact.published);
      });
    });

    // Collect from persisted messages
    messages?.forEach((msg) => {
      msg.content.forEach((block) => {
        if (block.type === 'artifact') {
          const a = block as ArtifactBlock;
          const artifactId = a.id + '-' + a.versionNumber;
          map.set(artifactId, { ...a, published: publishedLookup.get(artifactId) ?? false });
        }
      });
    });

    // Merge streamed artifact chunks
    chunks.forEach((chunk) => {
      if (!isContentBlock(chunk.content) || chunk.content.type !== 'artifact') return;
      const artifact = chunk.content as ArtifactBlock;
      const artifactId = artifact.id + '-' + artifact.versionNumber;
      const existing = map.get(artifactId);
      if (existing) {
        map.set(artifactId, {
          type: 'artifact',
          id: artifact.id,
          name: artifact.name ?? existing.name,
          artifactType: artifact.artifactType,
          content: [...existing.content, ...artifact.content],
          versionNumber: existing.versionNumber ?? artifact.versionNumber,
          published: existing.published,
        });
      } else {
        map.set(artifactId, { ...artifact, content: [...artifact.content], published: undefined });
      }
    });

    // Latest artifact is the last key in insertion order
    const keys = [...map.keys()];
    return { artifactsById: map, latestArtifactId: keys.length ? keys[keys.length - 1] : null };
  }, [messages, chunks]);

  // Same as below - likely will need to rework
  const referenceIndexMap = useMemo(() => {
    const map = new Map<string, { index: number; referenceType: ReferenceType }>();
    let index = 1;
    const track = (id: string, refType: ReferenceType) => {
      if (!map.has(id)) map.set(id, { index: index++, referenceType: refType });
    };
    const trackBlock = (block: ContentBlock) => {
      if (block.type === 'reference') {
        track(block.id, block.refType);
      }
      if (block.type === 'artifact') {
        block.content?.forEach(trackBlock);
      }
      if (block.type === 'inconsistency') {
        block.description?.forEach(trackBlock);
        block.references?.forEach((incRef) => track(incRef.reference.id, incRef.reference.refType));
      }
      if (block.type === 'document_inconsistencies') {
        block.content?.forEach(trackBlock);
      }
    };
    messages?.forEach((msg) => msg.content.forEach(trackBlock));
    chunks.forEach((chunk) => {
      if (isContentBlock(chunk.content)) trackBlock(chunk.content);
    });
    return map;
  }, [messages, chunks]);

  const handleDownload = useCallback(
    async (options: DocumentDownloadOptions, includeSourceDocuments?: boolean) => {
      setShowDownloadModal(false);

      if (!downloadMessageId) return;

      let title = threadName || 'Kim Draft';
      let artifactId: string | undefined;
      let artifactVersionNumber: number | undefined;

      trackCustomEvent(downloadArtifactId ? TrackingEventsV2.KimDownloadArtefact : TrackingEventsV2.KimDownloadAnswer, {
        matterId,
        subsetId,
        threadId,
      });

      if (downloadArtifactId) {
        const artifact = artifactsById.get(downloadArtifactId);
        if (artifact?.name) title = artifact.name;
        // downloadArtifactId is a composite key: `${uuid}-${versionNumber}`
        const lastDash = downloadArtifactId.lastIndexOf('-');
        artifactId = downloadArtifactId.slice(0, lastDash);
        artifactVersionNumber = Number(downloadArtifactId.slice(lastDash + 1));
      }

      if (includeSourceDocuments) {
        if (currentExportId) {
          setShowExportProgressModal(true);
          return;
        }
        try {
          setExportError(undefined);
          const exportId = uuidv4();
          setCurrentExportId(exportId);

          await startExport(exportId, title, downloadMessageId, artifactId, artifactVersionNumber, options);
          setIsCreatingExport(true);
          setShowExportProgressModal(true);
        } catch (error) {
          setCurrentExportId(null);
          setExportError(error instanceof Error ? error.message : 'Failed to start export');
          setShowExportProgressModal(true);
        }
      } else {
        setDownloadBusy(true);
        exportToWord(
          { title, messageId: downloadMessageId, artifactId, artifactVersionNumber, options },
          { onSettled: () => setDownloadBusy(false) },
        );
      }
    },
    [currentExportId, downloadMessageId, downloadArtifactId, artifactsById, threadName, startExport, exportToWord],
  );

  const collectedReferences = useMemo(
    () =>
      Array.from(referenceIndexMap.entries()).map(([id, { index, referenceType }]) => ({ id, index, referenceType })),
    [referenceIndexMap],
  );

  // Scroll to and highlight a specific artifact when navigating from the artifacts page
  const artifactIsInList = highlightArtifactId != null && artifactsById.has(highlightArtifactId);

  useLayoutEffect(() => {
    if (!artifactIsInList) return;
    const container = scrollContainerRef.current;
    if (!container) return;

    const el = container.querySelector(`[data-artifact-id="${highlightArtifactId}"]`);
    if (!el) return;

    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    el.classList.add('ring-1', 'ring-teal-400', 'ring-offset-2', 'transition-shadow', 'duration-500');

    setTimeout(() => {
      el.classList.remove('ring-1', 'ring-teal-400', 'ring-offset-2');
    }, 2000);

    setSearchParams({}, { replace: true });
  }, [highlightArtifactId, artifactIsInList]);

  // Group consecutive ai-search chunks into separate windows.
  // A new window starts whenever a non-ai-search chunk appears between ai-search chunks.
  const aiSearchGroups = useMemo(() => {
    const groups: { chunks: KimStreamChunk[]; items: string[]; timestamp: number }[] = [];
    let sawNonTemp = false;

    for (const chunk of chunks) {
      if (chunk.type === KimMessageType.AiSearch) {
        const lastGroup = groups[groups.length - 1];
        if (!lastGroup || sawNonTemp) {
          // Start a new group
          groups.push({ chunks: [chunk], items: [], timestamp: chunk.timestamp });
          sawNonTemp = false;
        } else {
          lastGroup.chunks.push(chunk);
        }
        // Extract text items
        if (chunk.content.type === 'text') {
          groups[groups.length - 1].items.push((chunk.content as { type: 'text'; value: string }).value);
        }
      } else {
        sawNonTemp = true;
      }
    }

    return groups;
  }, [chunks]);

  // This currently groups streamed chunks every time a new chunk comes through - I don't think this
  // will work long-term. I think we'll need a different approach with even types like message
  // start/add to message/message end to more efficiently group chunks.
  const groupedChunks = useMemo(() => {
    const groups: { type: KimMessageType; content: ContentBlock[]; isComplete: boolean; timestamp: number }[] = [];
    for (const chunk of chunks) {
      // Skip ai-search and compacting chunks — they're rendered separately
      if (chunk.type === KimMessageType.AiSearch) continue;
      if (chunk.type === KimMessageType.Compacting) continue;
      if (chunk.type === 'composing') continue;

      if (chunk.content.type === 'start') continue;

      if (chunk.content.type === 'end') {
        const last = groups[groups.length - 1];
        if (last) last.isComplete = true;
        continue;
      }

      const last = groups[groups.length - 1];

      if (last && last.type === chunk.type && chunk.type !== KimMessageType.AiReasoning) {
        if (chunk.content.type === 'artifact') {
          mergeArtifactIntoContent(last.content, chunk.content);
        } else {
          last.content.push(chunk.content);
        }
      } else {
        groups.push({
          type: chunk.type as KimMessageType,
          content: [chunk.content],
          isComplete: false,
          timestamp: chunk.timestamp,
        });
      }
    }
    return groups;
  }, [chunks]);

  // Track compacting events from the stream
  const compactingEvents = useMemo(() => {
    const events: { isComplete: boolean; timestamp: number }[] = [];
    for (const chunk of chunks) {
      if (chunk.type !== KimMessageType.Compacting) continue;
      if (chunk.content.type === 'start') {
        events.push({ isComplete: false, timestamp: chunk.timestamp });
      } else if (chunk.content.type === 'end') {
        const last = events[events.length - 1];
        if (last) last.isComplete = true;
      }
    }
    return events;
  }, [chunks]);

  // Build a unified render list so each reasoning temp window stays at its timestamp position
  const streamItems = useMemo(() => {
    const items: (
      | { kind: 'message'; type: KimMessageType; content: ContentBlock[]; isComplete: boolean; timestamp: number }
      | { kind: 'ai-search'; groupIndex: number; items: string[]; timestamp: number }
      | { kind: 'compacting'; isComplete: boolean; timestamp: number }
    )[] = [];

    for (const group of groupedChunks) {
      items.push({ kind: 'message', ...group });
    }

    aiSearchGroups.forEach((group, idx) => {
      items.push({
        kind: 'ai-search',
        groupIndex: idx,
        items: group.items,
        timestamp: group.timestamp,
      });
    });

    for (const event of compactingEvents) {
      items.push({ kind: 'compacting', ...event });
    }

    items.sort((a, b) => a.timestamp - b.timestamp);
    return items;
  }, [groupedChunks, aiSearchGroups, compactingEvents]);

  const isThreadActive = isKimThreadActive(thread?.status);
  const isThreadSettled = isKimThreadSettled(thread?.status);

  // True when the agent hasn't finished responding to the user's last message.
  // Deliberately stream/message-based only — thread status arrives on a 5s poll
  // and is tracked separately (isThreadActive/isThreadSettled). Every run-ending
  // path that settles the status also persists an AI message, so this terminates
  // without consulting status.
  const isAgentBusy = useMemo(() => {
    // If there's an AI stream message, check if it's still streaming
    const lastAiStream = streamItems.findLast((item) => item.kind === 'message' && item.type === KimMessageType.Ai);
    if (lastAiStream?.kind === 'message') return !lastAiStream.isComplete;

    // No AI stream content — check if DB has an AI response after the last user message
    if (!messagesLoaded || !messages?.length) return false;
    const lastUserIdx = messages.findLastIndex((m) => m.type === KimMessageType.User);
    if (lastUserIdx === -1) return false;
    return !messages.slice(lastUserIdx + 1).some((m) => m.type === KimMessageType.Ai);
  }, [messagesLoaded, messages, streamItems]);

  // True when Kim is composing a long-form response (stream event or thread status on reload)
  const isComposing = useMemo(() => {
    if (!isAgentBusy) return false;
    const composingChunks = chunks.filter((chunk) => chunk.type === 'composing');
    if (composingChunks.length > 0) {
      return composingChunks[composingChunks.length - 1].content.type === 'start';
    }

    return thread?.status === KimThreadStatus.Composing;
  }, [chunks, thread?.status, isAgentBusy]);

  // Focus the input when an AI answer finishes streaming
  const prevAgentBusyRef = useRef(isAgentBusy);
  useEffect(() => {
    if (prevAgentBusyRef.current && !isAgentBusy) {
      kimInputRef.current?.focus();
    }
    prevAgentBusyRef.current = isAgentBusy;
  }, [isAgentBusy]);

  // A request is "in flight" from when the user sends until the agent's answer
  // lands.
  useEffect(() => {
    setIsInFlight((isSending || isAgentBusy || isThreadActive) && !isThreadSettled);
  }, [isSending, isAgentBusy, isThreadActive, isThreadSettled]);

  // Resolve the streamed AI message's persisted DB id
  const [dbIdForStreamAiMessageId, setDbIdForStreamAiMessageId] = useState<string | undefined>();
  const resolvedStreamTimestampRef = useRef<number | undefined>();

  const completedAiStreamTimestamp = useMemo(() => {
    const lastAi = streamItems.findLast((i) => i.kind === 'message' && i.type === KimMessageType.Ai);
    return lastAi?.kind === 'message' && lastAi.isComplete ? lastAi.timestamp : undefined;
  }, [streamItems]);

  useEffect(() => {
    if (!completedAiStreamTimestamp) return;
    if (resolvedStreamTimestampRef.current === completedAiStreamTimestamp) return;
    resolvedStreamTimestampRef.current = completedAiStreamTimestamp;

    fetchFreshMessages()
      .then((msgs) => {
        const lastAi = msgs.findLast((m) => m.type === KimMessageType.Ai);
        if (lastAi) setDbIdForStreamAiMessageId(String(lastAi.id));
      })
      .catch(() => {});
  }, [completedAiStreamTimestamp, fetchFreshMessages]);

  // Unified display list: combines DB messages and stream items, grouping consecutive reasoning
  const displayItems = useMemo(() => {
    type LeafItem =
      | {
          kind: 'db-message';
          key: string;
          msgId: string;
          type: KimMessageType;
          content: ContentBlock[];
          createdDate: string;
          files?: KimThreadFile[];
        }
      | {
          kind: 'stream-message';
          key: string;
          streamIndex: number;
          type: KimMessageType;
          content: ContentBlock[];
          isComplete: boolean;
          timestamp: number;
        }
      | { kind: 'ai-search'; key: string; items: string[]; isFromDb: boolean }
      | { kind: 'db-compacting'; key: string }
      | { kind: 'stream-compacting'; key: string; isComplete: boolean };

    type DisplayItem =
      | { kind: 'reasoning-group'; key: string; isFollowedByAi: boolean; children: LeafItem[] }
      | LeafItem;

    const flat: Array<{ isReasoning: boolean; isAi: boolean; leaf: LeafItem }> = [];

    for (const msg of messages ?? []) {
      if (msg.type === KimMessageType.Compacting) {
        flat.push({
          isReasoning: false,
          isAi: false,
          leaf: { kind: 'db-compacting', key: `db-${msg.id}` },
        });
      } else if (msg.type === KimMessageType.AiSearch) {
        const items = msg.content
          .filter((block): block is Extract<ContentBlock, { type: 'text' }> => block.type === 'text')
          .map((block) => block.value);
        flat.push({
          isReasoning: true,
          isAi: false,
          leaf: {
            kind: 'ai-search',
            key: `db-${msg.id}`,
            items,
            isFromDb: true,
          },
        });
      } else {
        flat.push({
          isReasoning: msg.type === KimMessageType.AiReasoning,
          isAi: msg.type === KimMessageType.Ai,
          leaf: {
            kind: 'db-message',
            key: `db-${msg.id}`,
            msgId: String(msg.id),
            type: msg.type,
            content: msg.content,
            createdDate: String(msg.createdDate),
            files: msg.files,
          },
        });
      }
    }

    for (const [i, si] of streamItems.entries()) {
      if (si.kind === 'ai-search') {
        flat.push({
          isReasoning: true,
          isAi: false,
          leaf: {
            kind: 'ai-search',
            key: `ai-search-${si.groupIndex}`,
            items: si.items,
            isFromDb: false,
          },
        });
      } else if (si.kind === 'compacting') {
        flat.push({
          isReasoning: false,
          isAi: false,
          leaf: { kind: 'stream-compacting', key: `compacting-${i}`, isComplete: si.isComplete },
        });
      } else {
        flat.push({
          isReasoning: si.type === KimMessageType.AiReasoning,
          isAi: si.type === KimMessageType.Ai,
          leaf: {
            kind: 'stream-message',
            key: `stream-${i}`,
            streamIndex: i,
            type: si.type,
            content: si.content,
            isComplete: si.isComplete,
            timestamp: si.timestamp,
          },
        });
      }
    }

    const result: DisplayItem[] = [];
    let buffer: LeafItem[] = [];
    let groupCounter = 0;

    const flush = (isFollowedByAi: boolean) => {
      if (buffer.length > 0) {
        result.push({
          kind: 'reasoning-group',
          key: `rg-${groupCounter++}`,
          isFollowedByAi,
          children: [...buffer],
        });
        buffer = [];
      }
    };

    for (const { isReasoning, isAi, leaf } of flat) {
      if (isReasoning) {
        buffer.push(leaf);
      } else {
        flush(isAi);
        result.push(leaf);
      }
    }
    flush(false);

    return result;
  }, [messages, streamItems]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 300;
    const lastItem = displayItems[displayItems.length - 1];
    const userJustSentMessage = lastItem?.kind === 'db-message' && lastItem.type === KimMessageType.User;

    if ((isAtBottom && !userScrolledUpRef.current) || userJustSentMessage) {
      setTimeout(() => {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      }, 50);
    }
  }, [displayItems, chunks.length]);

  // Download handler for completed stream messages: ensures fresh DB messages
  // are available so we can resolve the persisted message ID for the download API.
  const handleStreamDownloadClick = useCallback(
    async (artifactId?: string) => {
      await queryClient.refetchQueries([QueryEntity.KimMessages, { subsetId, threadId }]);
      const freshMessages = queryClient.getQueryData<{ id: string; type: KimMessageType }[]>([
        QueryEntity.KimMessages,
        { subsetId, threadId },
      ]);
      const lastAiMsg = freshMessages?.findLast((m) => m.type === KimMessageType.Ai);
      if (lastAiMsg) {
        setDownloadMessageId(String(lastAiMsg.id));
        handleDownloadClick(artifactId);
      }
    },
    [queryClient, subsetId, threadId, handleDownloadClick],
  );

  const renderDisplayLeaf = (item: (typeof displayItems)[number]) => {
    switch (item.kind) {
      case 'db-message':
        return (
          <KimMessage
            key={item.key}
            messageId={item.msgId}
            type={item.type}
            content={item.content}
            isComplete
            referenceIndexMap={referenceIndexMap}
            onReferenceClick={openSidePanel}
            onDownloadClick={(artifactId?: string) => {
              setDownloadMessageId(item.msgId);
              handleDownloadClick(artifactId);
            }}
            artifactsById={artifactsById}
            latestArtifactId={latestArtifactId}
            isDownloading={downloadBusy}
            createdAt={item.createdDate}
            files={item.files}
            isOwnThread={thread && user && thread.createdById === user.id}
          />
        );
      case 'stream-message':
        return (
          <KimMessage
            key={item.key}
            messageId={item.isComplete ? dbIdForStreamAiMessageId : undefined}
            type={item.type}
            content={item.content}
            isComplete={item.isComplete}
            referenceIndexMap={referenceIndexMap}
            onReferenceClick={openSidePanel}
            onDownloadClick={item.isComplete ? handleStreamDownloadClick : undefined}
            artifactsById={artifactsById}
            latestArtifactId={latestArtifactId}
            isDownloading={downloadBusy}
            isOwnThread={thread && user && thread.createdById === user.id}
          />
        );
      case 'ai-search':
        return <AiSearchWindow key={item.key} items={item.items} isFromDb={item.isFromDb} />;
      case 'db-compacting':
        return <CompactingMessage key={item.key} isComplete />;
      case 'stream-compacting':
        return <CompactingMessage key={item.key} isComplete={item.isComplete} />;
      default:
        return null;
    }
  };

  const [dismissedSurveyIds, setDismissedSurveyIds] = useState<Set<string>>(new Set());

  const activeSurvey = useMemo<SurveyBlock | null>(() => {
    // Collect survey blocks and user messages across DB + stream in chronological order.
    // The active survey is the most recent one with no user message sent after it.
    type Entry = { timestamp: number; isUser: boolean; survey?: SurveyBlock };
    const entries: Entry[] = [];

    for (const msg of messages ?? []) {
      const timestamp = new Date(msg.createdDate).getTime();
      if (msg.type === KimMessageType.User) {
        entries.push({ timestamp, isUser: true });
        continue;
      }
      const survey = msg.content.find((b): b is SurveyBlock => b.type === 'survey');
      if (survey) entries.push({ timestamp, isUser: false, survey });
    }

    for (const si of streamItems) {
      if (si.kind !== 'message') continue;
      if (si.type === KimMessageType.User) {
        entries.push({ timestamp: si.timestamp, isUser: true });
        continue;
      }
      const survey = si.content.find((b): b is SurveyBlock => b.type === 'survey');
      if (survey) entries.push({ timestamp: si.timestamp, isUser: false, survey });
    }

    entries.sort((a, b) => a.timestamp - b.timestamp);

    for (let i = entries.length - 1; i >= 0; i--) {
      const e = entries[i];
      if (e.isUser) return null;
      if (e.survey) return e.survey;
    }
    return null;
  }, [messages, streamItems]);

  const showSurvey = activeSurvey !== null && !dismissedSurveyIds.has(activeSurvey.id);

  const [optimisticInterrupt, setOptimisticInterrupt] = useState(false);

  // Sending is gated on the stream alone (same check the release branch uses):
  // isAgentBusy holds until the run-terminal EndOfStreamBlock arrives, with the
  // DB fallback initialising the state after a reload. Thread status is not
  // consulted here — it lags behind the stream by up to one poll cycle and is
  // only authoritative for the stop button (isThreadActive) and recovery.
  const sendDisabled = isSending || isAgentBusy || isComposing;

  const handleSurveySubmit = (formatted: string) => {
    if (sendDisabled || isUploading) return;
    setOptimisticInterrupt(false);

    sendMessage(
      { message: formatted },
      {
        onSuccess: () => {
          trackCustomEvent(TrackingEventsV2.KimSendMessage, { matterId, subsetId, threadId });
        },
      },
    );
  };

  const handleSendMessage = () => {
    const text = inputValue.trim();
    if (!text || isUploading || sendDisabled) return;

    const files = uploadedFiles.length > 0 ? uploadedFiles : undefined;
    setOptimisticInterrupt(false);

    sendMessage(
      { message: text, files, mentions: extractKimMentions(text) },
      {
        onSuccess: () => {
          trackCustomEvent(TrackingEventsV2.KimSendMessage, { matterId, subsetId, threadId });
          setInputValue('');
          clearFiles();
        },
      },
    );
  };

  const handleInterrupt = () => {
    setOptimisticInterrupt(true);
    sendInterrupt(undefined, { onError: () => setOptimisticInterrupt(false) });
  };

  // Clear any pending interrupt state when switching threads
  useEffect(() => {
    setOptimisticInterrupt(false);
  }, [threadId]);

  useEffect(() => {
    if (isThreadSettled) setOptimisticInterrupt(false);
  }, [isThreadSettled]);

  const { hasPermission } = useHasPermission();
  const canCreateThread = hasPermission(Permission.ThreadCreate);

  if (threadError?.code === 404) {
    return (
      <Empty className="mb-12">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icon className="lucide--circle-question-mark" />
          </EmptyMedia>
          <EmptyTitle>Thread Not Found</EmptyTitle>
          <EmptyDescription>We couldn't find this Kim thread. It might have been deleted.</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div className="relative flex h-full w-full">
      {threadLoading ? (
        <div className="absolute top-2 left-0 flex items-center gap-1">
          <Skeleton className="h-6 w-16 bg-gray-100" />
        </div>
      ) : (
        <div className="absolute top-2 left-0 z-10">
          {thread && user && thread.createdById !== user.id ? (
            <div className="flex items-center gap-1 rounded-md border bg-white px-2 py-1 text-xs text-gray-500">
              <Icon className="lucide--user-2" />
              Thread created by <strong className="font-medium text-gray-700">{thread?.createdBy.email}</strong>
            </div>
          ) : (
            <SharePopover />
          )}
        </div>
      )}

      {/* Message column */}
      <div className="relative mr-2 flex min-w-0 grow flex-col pb-0">
        {/* References key */}
        <ReferenceKey />

        <div className="relative flex w-full grow flex-col justify-between overflow-hidden">
          <div
            ref={scrollContainerRef}
            onScroll={checkScrollToBottom}
            className={cn(
              thread && user && thread.createdById !== user.id
                ? 'mask-b-from-[calc(100%-6rem)]'
                : '-mb-6 mask-b-from-95%',
              '[container-type:inline-size] min-h-0 grow space-y-4 overflow-y-auto',
            )}
          >
            {messagesLoading && (
              <div className="my-8 animate-pulse text-center text-sm text-gray-400">Loading messages...</div>
            )}

            <div key={threadId} ref={scrollContentRef} className="mx-auto max-w-4xl pt-24">
              {displayItems.map((item) =>
                item.kind === 'reasoning-group' ? (
                  <CollapsibleReasoning key={item.key} isComplete={item.isFollowedByAi}>
                    {item.children.map((child) => renderDisplayLeaf(child))}
                  </CollapsibleReasoning>
                ) : (
                  renderDisplayLeaf(item)
                ),
              )}

              {isComposing && (
                <ComposingMessage
                  enableNotifications={thread?.enableNotifications ?? false}
                  canToggleNotifications={!!(thread && user && thread.createdById === user.id)}
                />
              )}

              {isAgentBusy &&
                !isComposing &&
                thread?.status !== KimThreadStatus.Failed &&
                !streamItems.some((item) => item.kind === 'message' && item.type === KimMessageType.Ai) && (
                  <div className="mx-auto my-4 max-w-3xl px-4">
                    <Icon className="lucide--loader animate-spin text-base text-gray-400 ease-in-out" />
                  </div>
                )}
            </div>

            <div ref={trackingElementRef} />
            <div ref={spacerRef} />
          </div>

          <AnimatePresence initial={false}>
            <div className="relative -m-1 mx-auto w-full max-w-3xl p-1 pt-0">
              <div className="pointer-events-none absolute bottom-full flex w-full flex-col items-center pr-2">
                <ScrollToBottomButton
                  visible={showScrollToBottom}
                  onClick={() =>
                    scrollContainerRef.current?.scrollTo({
                      top: scrollContainerRef.current.scrollHeight,
                      behavior: 'smooth',
                    })
                  }
                />

                {thread?.status === KimThreadStatus.Failed && (
                  <motion.div
                    key="error-state"
                    initial={{ opacity: 0, filter: 'blur(2px)' }}
                    animate={{ opacity: 1, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, filter: 'blur(2px)' }}
                    transition={{ duration: 0.15 }}
                    className="mb-2 w-full"
                  >
                    <KimErrorState />
                  </motion.div>
                )}

                {thread?.isLegacy && (
                  <motion.div
                    key="legacy-thread"
                    initial={{ opacity: 0, filter: 'blur(2px)' }}
                    animate={{ opacity: 1, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, filter: 'blur(2px)' }}
                    transition={{ duration: 0.15 }}
                    className="flex w-full gap-2.5 rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-500"
                  >
                    <Icon className="lucide--history mt-0.75" />
                    <span>
                      This is a legacy thread from the previous version of Kim. It's read-only and can't be continued.
                    </span>
                  </motion.div>
                )}

                {!thread?.isLegacy && thread && user && thread.createdById !== user.id && (
                  <motion.div
                    key="cannot-message"
                    initial={{ opacity: 0, filter: 'blur(2px)' }}
                    animate={{ opacity: 1, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, filter: 'blur(2px)' }}
                    transition={{ duration: 0.15 }}
                    className="flex w-full gap-2.5 rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-500"
                  >
                    <Icon className="lucide--info mt-0.75" />
                    <span>You can't send messages to a thread created by another user.</span>
                  </motion.div>
                )}
              </div>
              {!thread?.isLegacy &&
                !(thread && user && thread.createdById !== user.id) &&
                (canCreateThread ? (
                  showSurvey && activeSurvey ? (
                    <motion.div
                      key={`survey-${activeSurvey.id}`}
                      initial={{ opacity: 0, filter: 'blur(2px)' }}
                      animate={{ opacity: 1, filter: 'blur(0px)' }}
                      exit={{ opacity: 0, filter: 'blur(2px)' }}
                      transition={{ duration: 0.15 }}
                    >
                      <SurveyBlockComponent
                        questions={activeSurvey.questions}
                        onSubmit={handleSurveySubmit}
                        onDismiss={() =>
                          setDismissedSurveyIds((prev) => {
                            const next = new Set(prev);
                            next.add(activeSurvey.id);
                            return next;
                          })
                        }
                        disabled={sendDisabled || isUploading}
                      />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="kim-input"
                      initial={{ opacity: 0, filter: 'blur(2px)' }}
                      animate={{ opacity: 1, filter: 'blur(0px)' }}
                      exit={{ opacity: 0, filter: 'blur(2px)' }}
                      transition={{ duration: 0.15 }}
                    >
                      <KimInput
                        ref={kimInputRef}
                        className="max-h-100 min-h-16 p-4 py-3 pb-0"
                        placeholder="Ask Kim..."
                        value={inputValue}
                        onChange={setInputValue}
                        onSubmit={handleSendMessage}
                        onInterrupt={handleInterrupt}
                        isGenerating={isThreadActive && isAgentBusy && !isSending && !optimisticInterrupt}
                        isInterrupting={optimisticInterrupt && isAgentBusy}
                        sendDisabled={sendDisabled}
                        disabled={!messages || !thread || thread.status === KimThreadStatus.Failed}
                        uploadedFiles={uploadedFiles}
                        isUploading={isUploading}
                        onFilesAdded={addFiles}
                        onFileRemove={removeFile}
                      />
                    </motion.div>
                  )
                ) : (
                  <motion.div
                    key="no-perms-message"
                    initial={{ opacity: 0, filter: 'blur(2px)' }}
                    animate={{ opacity: 1, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, filter: 'blur(2px)' }}
                    transition={{ duration: 0.15 }}
                    className="flex w-full gap-2.5 rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-500"
                  >
                    <Icon className="lucide--lock mt-0.75" />
                    <span>You don't have permission to send messages. Speak to an administrator to gain access.</span>
                  </motion.div>
                ))}
              <VerifyDisclaimer />
            </div>
          </AnimatePresence>
        </div>
      </div>

      {/* Download dialogs */}
      <ThreadDownloadDialog
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onDownload={handleDownload}
        isLoading={downloadBusy}
      />

      {currentExportId && (
        <GenericExportProgressModal<BaseExportEvent>
          isOpen={showExportProgressModal}
          onClose={() => {
            setShowExportProgressModal(false);
            setIsCreatingExport(false);
          }}
          exportConfig={{
            progressEndpoint: getProgressEndpoint(),
            exportId: currentExportId,
          }}
          itemName="Kim Answer"
          isCreating={isCreatingExport}
          error={exportError}
        />
      )}

      {/* Reference side panel - conditionally rendered */}
      <AnimatePresence>
        {sidePanel.isOpen && sidePanel.activeId && sidePanel.activeType && (
          <ReferenceSidePanel
            onClose={closeSidePanel}
            references={collectedReferences}
            activeReferenceId={sidePanel.activeId}
            activeReferenceType={sidePanel.activeType}
            onSelectReference={openSidePanel}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
